import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
  IncomingCreatePrePurchaseFormDto,
  IncomingCreatePrePurchaseResponseDto,
  IncomingUpdatePrePurchaseFormDto,
  IncomingUpdatePrePurchaseResponseDto,
  OutgoingPrePurchaseFormDto,
  OutgoingPrePurchaseResponseDto
} from 'api/dtos/events/pre-purchase-questions';
import {
  createPrePurchaseForm,
  createPrePurchaseResponse,
  deletePrePurchaseForm,
  getPrePurchaseForm,
  getPrePurchaseResponse,
  updatePrePurchaseForm,
  updatePrePurchaseResponse
} from 'api/services/events/pre-purchase-questions';
import { ErrorResponse } from 'api/types/errors';

export function useCreatePrePurchaseFormMutation() {
  return useMutation<
    OutgoingPrePurchaseFormDto,
    ErrorResponse,
    { eventId: string; schema: IncomingCreatePrePurchaseFormDto }
  >(({ eventId, schema }) => createPrePurchaseForm({ eventId, schema }));
}

export function useUpdatePrePurchaseFormMutation() {
  return useMutation<
    OutgoingPrePurchaseFormDto,
    ErrorResponse,
    { eventId: string; schema: IncomingUpdatePrePurchaseFormDto }
  >(({ eventId, schema }) => updatePrePurchaseForm({ eventId, schema }));
}

export function useCreatePrePurchaseResponseMutation() {
  return useMutation<
    OutgoingPrePurchaseResponseDto,
    ErrorResponse,
    { eventId: string; response: IncomingCreatePrePurchaseResponseDto }
  >(({ eventId, response }) =>
    createPrePurchaseResponse({ eventId, response })
  );
}

export function useUpdatePrePurchaseResponseMutation() {
  return useMutation<
    OutgoingPrePurchaseResponseDto,
    ErrorResponse,
    { eventId: string; response: IncomingUpdatePrePurchaseResponseDto }
  >(({ eventId, response }) =>
    updatePrePurchaseResponse({ eventId, response })
  );
}

export function useGetPrePurchaseForm(
  { eventId }: { eventId: string },
  options?: { enabled?: boolean }
) {
  return useQuery<OutgoingPrePurchaseFormDto, ErrorResponse>(
    ['prePurchaseForm', { eventId }],
    () => getPrePurchaseForm(eventId),
    {
      enabled: options?.enabled
    }
  );
}

export function useGetPrePurchaseResponse(
  { eventId }: { eventId: string },
  options?: { enabled?: boolean }
) {
  return useQuery<OutgoingPrePurchaseResponseDto, ErrorResponse>(
    ['prePurchaseResponse', { eventId }],
    () => getPrePurchaseResponse(eventId),
    {
      enabled: options?.enabled,
      retry: 0
    }
  );
}

export function useDeletePrePurchaseForm() {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, ErrorResponse, string>(
    deletePrePurchaseForm,
    {
      onSuccess: (_, eventId) => {
        queryClient.resetQueries(['prePurchaseForm', { eventId }]);
      }
    }
  );
}
